import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Dispatch, connect } from 'react-redux';
import { toast } from 'react-toastify';
import { Button, FormControl } from 'react-bootstrap';
import { IApplicationState, AppActionCreators, UserActionCreators, IUserLookupDataResponse, IAppState } from '@nkr/store/index';
import { IAuthRole, IBaseResponse, ICenterBase, IGroup, IListItem, IUser, IUserListResponse, IUserResponse } from '@nkr/models/index';
import { OrganType, UserGroup } from '@nkr/enums/index';
import { BodyScrollEvent, ColumnApi, FirstDataRenderedEvent, GridApi, GridReadyEvent } from 'ag-grid-community';
import { IAddEditData } from './add-edit-data';
import { UserAction } from './action-button-renderer';
import Drawer from 'rc-drawer';
import AddEdit from './add-edit';
import ConfirmAction from './confirm-action';
import ConfirmDeactivation from './confirm-deactivation';
import UserListGrid from './user-list-grid';

interface IOwnProps {
    centerId: number,
    isAdmin: boolean
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getLookupData: () => Promise<IUserLookupDataResponse>,
    getUsers: (isAdmin: boolean, centerId: number, organType: OrganType) => Promise<IUserListResponse>,
    deleteUser: (isAdmin: boolean, centerId: number, userId: number) => Promise<IBaseResponse>,
    saveUser: (isAdmin: boolean, id: number, firstName: string, lastName: string, email: string, username: string, password: string, groupId: number, courierVendor: number, isMicrositeCoach: boolean, contactId: number, centerId: number, roles: number[], sendTicketNotification: boolean, isDeactivated: boolean, organType: OrganType, opoId: number) => Promise<IUserResponse>,
    updatePasswordExpiration: (isAdmin: boolean, centerId: number, id: number, activate: boolean) => Promise<IBaseResponse>,
    unblockUser: (isAdmin: boolean, centerId: number, id: number, ipAddress: string) => Promise<IBaseResponse>,
    forceUnlockUser: (isAdmin: boolean, centerId: number, id: number) => Promise<IBaseResponse>,
    forceLockUser: (isAdmin: boolean, centerId: number, id: number) => Promise<IBaseResponse>
}

type TProps =
    IOwnProps
    & IStateProps
    & IDispatchProps;

interface IState {
    users: IUser[],
    groupId: number,
    organType: number,
    centerId: number,
    micrositeCoachOnly: number,
    search: string,
    lookupData: {
        userGroupList: IGroup[],
        roleList: IAuthRole[],
        centerList: ICenterBase[],
        micrositeCoachContactList: IListItem[],
        courierVendorList: IListItem[],
        organTypeList: IListItem[],
        opoList: IListItem[]
    },
    addEdit: {
        show: boolean,
        data: IAddEditData,
        uniqueId: string
    },
    confirmAction: {
        show: boolean,
        action: UserAction,
        data: IUser
    },
    confirmDeactivation: {
        show: boolean,
        closeDetails: boolean
    },
    menuId: number,
    menuDisplayed: boolean
}

const initialState: IState = {
    users: [],
    groupId: 0,
    organType: -1,
    centerId: 0,
    micrositeCoachOnly: 0,
    search: '',
    lookupData: {
        userGroupList: [],
        roleList: [],
        centerList: [],
        micrositeCoachContactList: [],
        courierVendorList: [],
        organTypeList: [],
        opoList: []
    },
    addEdit: {
        show: false,
        data: null,
        uniqueId: null
    },
    confirmAction: {
        show: false,
        action: 0,
        data: null
    },
    confirmDeactivation: {
        show: false,
        closeDetails: false
    },
    menuId: 0,
    menuDisplayed: false
};

class UserList extends React.Component<TProps, IState> {
    readonly state: IState = initialState;

    gridApi: GridApi = null;
    gridColumnApi: ColumnApi = null;
    container: HTMLDivElement;

    onGridReady = (e: GridReadyEvent) => {
        this.gridApi = e.api;
        this.gridColumnApi = e.columnApi;

        e.api.sizeColumnsToFit();
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    onBodyScroll = (e: BodyScrollEvent) => {
        if (this.state.menuDisplayed) {
            document.body.click();
            this.setState({
                menuDisplayed: false
            });
        }
    }

    saveContainer = (container: HTMLDivElement) => {
        this.container = container;
    };

    getContainer = () => {
        return this.container;
    };

    componentDidMount() {
        this.getData();
    }

    getData = () => {
        let { isAdmin, centerId } = this.props;

        let organType = this.props.app.site.organType == OrganType.Kidney ? null : this.props.app.site.organType;

        return this.props.showLoading('Loading users, one moment please...').then(() => {
            return Promise.all([
                this.props.getUsers(isAdmin, centerId, organType),
                this.props.getLookupData()
            ])
                .then(([userResponse, lookupDataResponse]) => {
                    if (userResponse.data.success && lookupDataResponse.data.success) {
                        this.setState({
                            users: userResponse.data.results,
                            lookupData: lookupDataResponse.data.results
                        });
                    }

                    this.props.hideLoading();
                });
        });
    }

    refreshData = () => {
        let { isAdmin, centerId } = this.props;

        let organType = this.props.app.site.organType == OrganType.Kidney ? null : this.props.app.site.organType;

        this.props.getUsers(isAdmin, centerId, organType).then(response => {
            if (response.data.success) {
                this.setState({
                    users: response.data.results,
                });
            }

            this.props.hideLoading();
        });
    }

    getFilteredData = () => {
        return this.state.users.filter(user => {
            let groupFilter = this.state.groupId > 0 ? user.groupId == this.state.groupId : true;
            let centerFilter = this.state.centerId > 0 ? user.centerId == this.state.centerId : true;
            let userNameFilter = this.state.search != '' ? user.username.toUpperCase().indexOf(this.state.search.toUpperCase()) >= 0 : true;
            let emailFilter = this.state.search != '' ? user.email.toUpperCase().indexOf(this.state.search.toUpperCase()) >= 0 : true;
            let micrositeCoachFilter = this.state.micrositeCoachOnly == 1 ? user.isMicrositeCoach : true;
            let organTypeFilter = this.state.organType >= 0 ? user.organTypeId == this.state.organType : true;

            return groupFilter && centerFilter && (userNameFilter || emailFilter) && micrositeCoachFilter && organTypeFilter;
        });
    }

    edit = (userId: number) => {
        let user = this.state.users.find(x => x.id == userId);

        let { id, firstName, lastName, email, username, centerId, courierVendor, groupId,
            isMicrositeCoach, contactId, donorConnectCaseLoad, roles, sendTicketNotification, isDeactivated, mobileNumber, isTwoFactorEnabled, organTypeId, opoId } = user;

        let data: IAddEditData = {
            id,
            firstName,
            lastName,
            email,
            password: '',
            confirmPassword: '',
            username,
            centerId,
            courierVendor,
            groupId,
            organTypeId,
            isMicrositeCoach,
            contactId,
            donorConnectCaseLoad,
            sendTicketNotification,
            isDeactivated,
            mobileNumber,
            isTwoFactorEnabled,
            opoId,
            roles: roles.map(x => x.id)
        };

        this.setState({
            addEdit: {
                show: true,
                data,
                uniqueId: _.uniqueId()
            }
        });
    }

    confirm = (id: number, action: UserAction) => {
        let data = this.state.users.find(x => x.id == id);

        this.setState({
            confirmAction: {
                show: true,
                action,
                data
            }
        });
    }

    delete = (id: number) => {
        let { isAdmin, centerId } = this.props;

        this.setState({
            confirmAction: {
                show: false,
                action: 0,
                data: null
            }
        }, () => {
            this.props.showLoading('Deleting user, one moment please...').then(() => {
                this.props.deleteUser(isAdmin, centerId, id)
                    .then(response => {
                        this.props.hideLoading();

                        if (response.data.success) {
                            let users = _.cloneDeep(this.state.users).filter(x => x.id != id);

                            this.setState({
                                users
                            }, () => {
                                toast.success('User deleted!');
                            });

                            this.refreshData();
                        }
                    })
                    .catch((error: any) => {
                        this.props.hideLoading();
                        toast.error('Unable to delete user');
                    });
            });
        });
    }

    updatePasswordExpiration = (id: number, activate: boolean) => {
        let { isAdmin, centerId } = this.props;

        this.setState({
            confirmAction: {
                show: false,
                action: 0,
                data: null
            }
        }, () => {
            let message = activate ? 'Activating password expiration, one moment please...' : 'Deactivating password expiration, one moment please...';

            this.props.showLoading(message).then(() => {
                this.props.updatePasswordExpiration(isAdmin, centerId, id, activate)
                    .then(response => {
                        this.props.hideLoading();

                        if (response.data.success) {
                            let users = _.cloneDeep(this.state.users).map(x => {
                                if (x.id == id) {
                                    x.expirePasswordDateTime = activate ? moment().add(6, 'months').format('MM/DD/YYYY') : '';
                                }

                                return x;
                            });

                            this.setState({
                                users
                            }, () => {
                                toast.success(activate ? 'Password expiration activated successfully' : 'Password expiration deactivated successfully');
                            });

                            this.refreshData();
                        }
                    })
                    .catch(error => {
                        this.props.hideLoading();
                        toast.error(activate ? 'Unable to activate password expiration' : 'Unable to deactivate password expiration');
                    });
            });
        });
    }

    unblockUser = (id: number) => {
        let user = this.state.users.find(x => x.id == id);
        let ipAddress = user.lastLoginAttemptIp;

        let { isAdmin, centerId } = this.props;

        this.setState({
            confirmAction: {
                show: false,
                action: 0,
                data: null
            }
        }, () => {
            this.props.showLoading('Unblocking user, one moment please...').then(() => {
                this.props.unblockUser(isAdmin, centerId, id, ipAddress)
                    .then(response => {
                        this.props.hideLoading();

                        if (response.data.success) {
                            let users = _.cloneDeep(this.state.users).map(x => {
                                if (x.id == id) {
                                    x.isLockedOut = false;
                                    x.lastLoginAttemptCount = 0;
                                    x.lastLoginAttemptIp = '';
                                }

                                return x;
                            });

                            this.setState({
                                users
                            }, () => {
                                toast.success('User unlocked successfully');
                            });

                            this.refreshData();
                        }
                    })
                    .catch(error => {
                        this.props.hideLoading();
                        toast.error('Unable to unlock that user');
                    });
            });
        });
    }

    forceUnlockUser = (id: number) => {
        let { isAdmin, centerId } = this.props;

        this.setState({
            confirmAction: {
                show: false,
                action: 0,
                data: null
            }
        }, () => {
            this.props.showLoading('Unlocking user, one moment please...').then(() => {
                this.props.forceUnlockUser(isAdmin, centerId, id)
                    .then(response => {
                        this.props.hideLoading();

                        if (response.data.success) {
                            let users = _.cloneDeep(this.state.users).map(x => {
                                if (x.id == id) {
                                    x.isLockedOut = false;
                                }

                                return x;
                            });

                            this.setState({
                                users
                            }, () => {
                                toast.success('User unlocked successfully');
                            });

                            this.refreshData();
                        }
                    })
                    .catch(error => {
                        this.props.hideLoading();
                        toast.error('Unable to unlock that user');
                    });
            });
        });
    }

    forceLockUser = (id: number) => {
        let { isAdmin, centerId } = this.props;

        this.setState({
            confirmAction: {
                show: false,
                action: 0,
                data: null
            }
        }, () => {
            this.props.showLoading('Locking user, one moment please...').then(() => {
                this.props.forceLockUser(isAdmin, centerId, id)
                    .then(response => {
                        this.props.hideLoading();

                        if (response.data.success) {
                            let users = _.cloneDeep(this.state.users).map(x => {
                                if (x.id == id) {
                                    x.isLockedOut = true;
                                }

                                return x;
                            });

                            this.setState({
                                users
                            }, () => {
                                toast.success('User locked successfully');
                            });

                            this.refreshData();
                        }
                    })
                    .catch(error => {
                        this.props.hideLoading();
                        toast.error('Unable to lock that user');
                    });
            });
        });
    }

    handleMenuDisplayed = (menuId: number) => {
        this.setState({
            menuId
        });
    }

    handleMenuToggle = (show: boolean) => {
        this.setState({
            menuDisplayed: show
        });
    };

    handleItemSelected = (id: number, action: UserAction) => {
        switch (action) {
            case UserAction.Edit:
                this.edit(id)
                break;

            case UserAction.Delete:
            case UserAction.ActivatePasswordExpiration:
            case UserAction.DeactivatePasswordExpiration:
            case UserAction.ClearBlocks:
            case UserAction.UnlockUser:
            case UserAction.LockoutUser:
                this.confirm(id, action);
                break;
        }
    }

    handleGroupChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            groupId: parseInt(e.currentTarget.value)
        });
    }

    handleOrganTypeChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            organType: parseInt(e.currentTarget.value)
        });
    }

    handleCenterChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            centerId: parseInt(e.currentTarget.value)
        });
    }

    handleMicrositeCoachOnlyChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            micrositeCoachOnly: parseInt(e.currentTarget.value)
        });
    }

    handleSearchChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            search: e.currentTarget.value
        });
    }

    handleFieldChange = (field: string, value: any) => {
        let data = _.cloneDeep(this.state.addEdit.data);

        switch (field) {
            case 'groupId':
                data.groupId = parseInt(value);
                break;

            case 'contactId':
                data.contactId = parseInt(value);
                break;

            case 'isMicrositeCoach':
                data.isMicrositeCoach = value == '1';
                break;

            case 'sendTicketNotification':
                data.sendTicketNotification = value == '1';
                break;

            case 'isDeactivated':
                data.isDeactivated = value == '1';
                break;

            case 'courierVendor':
                data.courierVendor = parseInt(value);
                break;

            case 'centerId':
                data.centerId = parseInt(value);
                break;

            case 'opoId':
                data.opoId = parseInt(value);
                break;

            default:
                data[field] = value.toString();
        }

        this.setState({
            addEdit: { ...this.state.addEdit, data }
        });
    }

    handleRoleChange = (roleId: number) => {
        let data = _.cloneDeep(this.state.addEdit.data);

        if (data.roles.indexOf(roleId) >= 0) {
            data.roles = data.roles.filter(id => id != roleId);
        } else {
            data.roles.push(roleId);
        }

        this.setState({
            addEdit: { ...this.state.addEdit, data }
        });
    }

    handleAdd = () => {
        let organTypeId = this.props.app.site.organType == OrganType.Liver ? OrganType.Liver : null;

        this.setState({
            addEdit: {
                show: true,
                data: {
                    id: 0,
                    firstName: '',
                    lastName: '',
                    email: '',
                    password: '',
                    confirmPassword: '',
                    username: '',
                    centerId: this.props.isAdmin ? 0 : this.props.centerId,
                    groupId: this.props.isAdmin ? 0 : UserGroup.CenterUser,
                    organTypeId,
                    courierVendor: 0,
                    isMicrositeCoach: false,
                    contactId: 0,
                    donorConnectCaseLoad: 0,
                    roles: [],
                    sendTicketNotification: false,
                    isDeactivated: false,
                    mobileNumber: '',
                    isTwoFactorEnabled: false,
                    opoId: 0,
                },
                uniqueId: _.uniqueId()
            }
        });
    }

    handleSaveUser = (closeDetails: boolean) => {
        let { id, isDeactivated } = this.state.addEdit.data;
        let originalUser = this.state.users.find(x => x.id == id);
        let isDeactivatedChanged = originalUser != null && originalUser.isDeactivated != isDeactivated;

        if (isDeactivatedChanged) {
            this.setState({
                confirmDeactivation: { show: true, closeDetails }
            });

            return;
        }

        this.saveUser(closeDetails);
    }

    handleConfirmDeactivation = () => {
        let { closeDetails } = this.state.confirmDeactivation;

        this.setState({
            confirmDeactivation: { show: false, closeDetails: false }
        }, () => {
            this.saveUser(closeDetails);
        });
    }

    handleCancelDeactivationConfirm = () => {
        this.setState({
            confirmDeactivation: { show: false, closeDetails: false }
        });
    }

    saveUser = (closeDetails: boolean) => {
        let {
            id, firstName, lastName, email, username,
            password,
            groupId, courierVendor, isMicrositeCoach,
            contactId, centerId, roles,
            sendTicketNotification,
            isDeactivated,
            organTypeId,
            opoId
        } = this.state.addEdit.data;

        let { isAdmin } = this.props;

        this.props.showLoading('Saving user, one moment please..').then(() => {
            return this.props.saveUser(isAdmin, id, firstName, lastName, email, username, password, groupId, courierVendor, isMicrositeCoach, contactId, centerId, roles, sendTicketNotification, isDeactivated, organTypeId, opoId)
                .then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        toast.success('User saved successfully');

                        if (closeDetails) {
                            this.setState({
                                addEdit: { ...this.state.addEdit, show: false, data: null }
                            });
                        }

                        this.refreshData();
                    } else {
                        let message = '';

                        if (response.data.message == undefined) {
                            if (response.data.results.failureCode == 1) {
                                message = 'User already exists with that first name and last name';
                            }

                            if (response.data.results.failureCode == 2) {
                                message = 'User already exists with that user name';
                            }

                            if (response.data.results.failureCode == 3) {
                                message = 'User already exists with that email address';
                            }
                        } else {
                            message = response.data.message;
                        }

                        toast.error(
                            <div>
                                <div style={{ marginBottom: 10 }}>Unable to save the user</div>
                                <div className='text-danger'>
                                    {message}
                                </div>
                            </div>, { autoClose: false }
                        );
                    }
                });
        });
    }

    handleCancelAddEdit = () => {
        this.setState({
            addEdit: { ...this.state.addEdit, show: false, data: null }
        });
    }

    handleConfirmCancel = () => {
        this.setState({
            confirmAction: {
                show: false,
                action: 0,
                data: null
            }
        });
    }

    handleConfirmAction = () => {
        let { data, action } = this.state.confirmAction;

        if (action == UserAction.Delete) {
            this.delete(data.id);
        }

        if (action == UserAction.ActivatePasswordExpiration) {
            this.updatePasswordExpiration(data.id, true);
        }

        if (action == UserAction.DeactivatePasswordExpiration) {
            this.updatePasswordExpiration(data.id, false);
        }

        if (action == UserAction.ClearBlocks) {
            this.unblockUser(data.id);
        }

        if (action == UserAction.LockoutUser) {
            this.forceLockUser(data.id);
        }

        if (action == UserAction.UnlockUser) {
            this.forceUnlockUser(data.id)
        }
    }

    render() {
        const list = this.getFilteredData();

        const sortedGroupList = _.sortBy(this.state.lookupData.userGroupList, x => x.name);

        return (
            <>
                <div ref={this.saveContainer} />

                <div className='container-fluid'>
                    {this.props.isAdmin && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                            <div>
                                <h1 style={{ margin: 0 }}>Users</h1>
                            </div>
                        </div>
                    )}

                    <form>
                        <div className='well well-sm' style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginBottom: 20, padding: '9.5px 19px' }}>
                            <div style={{ display: 'flex' }}>
                                {this.props.isAdmin && (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 15 }}>
                                            <div style={{ marginRight: 5 }}>Filter&nbsp;by&nbsp;User&nbsp;Group:</div>
                                            <FormControl
                                                componentClass='select'
                                                value={this.state.groupId}
                                                onChange={this.handleGroupChange}
                                            >
                                                <option value={0}>- All Groups -</option>
                                                {sortedGroupList.map(group =>
                                                    <option value={group.id}>{group.name}</option>
                                                )}
                                            </FormControl>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 15 }}>
                                            <div style={{ marginRight: 5 }}>Filter&nbsp;by&nbsp;Organ&nbsp;Type:</div>
                                            <FormControl
                                                componentClass='select'
                                                value={this.state.organType}
                                                onChange={this.handleOrganTypeChange}
                                            >
                                                <option value={-1}>- All Types -</option>
                                                {this.state.lookupData.organTypeList.map(item =>
                                                    <option key={'org-t-' + item.id} value={item.id}>{item.description}</option>
                                                )}
                                            </FormControl>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 15 }}>
                                            <div style={{ marginRight: 5 }}>Filter&nbsp;by&nbsp;Center:</div>
                                            <FormControl
                                                componentClass='select'
                                                value={this.state.centerId}
                                                onChange={this.handleCenterChange}
                                            >
                                                <option value={0}>- All Centers -</option>
                                                {this.state.lookupData.centerList.map(center =>
                                                    <option value={center.id}>{center.alias}</option>
                                                )}
                                            </FormControl>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 15 }}>
                                            <div style={{ marginRight: 5 }}>Microsite&nbsp;Coaches&nbsp;Only:</div>
                                            <FormControl
                                                componentClass='select'
                                                value={this.state.micrositeCoachOnly}
                                                onChange={this.handleMicrositeCoachOnlyChange}
                                            >
                                                <option value={0}>No</option>
                                                <option value={1}>Yes</option>
                                            </FormControl>
                                        </div>
                                    </>
                                )}

                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ marginRight: 5 }}>Filter&nbsp;by&nbsp;Username/Email:</div>
                                    <FormControl
                                        id='search-by-name'
                                        name='search-by-name'
                                        autoComplete='off'
                                        value={this.state.search}
                                        onChange={this.handleSearchChange}
                                    />
                                </div>
                            </div>

                            <div>
                                <Button bsStyle='primary' onClick={this.handleAdd}>Add User</Button>
                            </div>
                        </div>
                    </form>

                    <UserListGrid
                        list={list}
                        isAdmin={this.props.isAdmin}
                        currentUserGroupId={this.props.app.user.groupId}
                        filterGroupId={this.state.groupId}
                        onItemSelected={this.handleItemSelected}
                    />
                    <div>{list.length} of {this.state.users.length} users...</div>
                </div>

                <ConfirmAction
                    show={this.state.confirmAction.show}
                    data={this.state.confirmAction.data}
                    action={this.state.confirmAction.action}
                    onConfirm={this.handleConfirmAction}
                    onCancel={this.handleConfirmCancel}
                />

                <ConfirmDeactivation
                    show={this.state.confirmDeactivation.show}
                    data={this.state.addEdit.data}
                    onConfirm={this.handleConfirmDeactivation}
                    onCancel={this.handleCancelDeactivationConfirm}
                />

                {this.props.isAdmin ?
                    <Drawer
                        width="30vw"
                        // handler={false}
                        placement="right"
                        getContainer={this.getContainer}
                        open={this.state.addEdit.show}
                    >
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <div style={{ padding: 10 }}>
                                <AddEdit
                                    key={this.state.addEdit.uniqueId}
                                    show={this.state.addEdit.show}
                                    data={this.state.addEdit.data}
                                    isAdmin={this.props.isAdmin}
                                    userGroups={this.state.lookupData.userGroupList}
                                    roles={this.state.lookupData.roleList}
                                    centers={this.state.lookupData.centerList}
                                    micrositeCoaches={this.state.lookupData.micrositeCoachContactList}
                                    courierVendors={this.state.lookupData.courierVendorList}
                                    organTypeList={this.state.lookupData.organTypeList}
                                    opoList={this.state.lookupData.opoList}
                                    onFieldChange={this.handleFieldChange}
                                    onRoleChange={this.handleRoleChange}
                                    onSave={this.handleSaveUser}
                                    onCancel={this.handleCancelAddEdit}
                                />
                            </div>
                        </div>
                    </Drawer>
                    :
                    <AddEdit
                        key={this.state.addEdit.uniqueId}
                        show={this.state.addEdit.show}
                        data={this.state.addEdit.data}
                        isAdmin={this.props.isAdmin}
                        userGroups={this.state.lookupData.userGroupList}
                        roles={this.state.lookupData.roleList}
                        centers={this.state.lookupData.centerList}
                        micrositeCoaches={this.state.lookupData.micrositeCoachContactList}
                        courierVendors={this.state.lookupData.courierVendorList}
                        organTypeList={this.state.lookupData.organTypeList}
                        opoList={[]}
                        onFieldChange={this.handleFieldChange}
                        onRoleChange={this.handleRoleChange}
                        onSave={this.handleSaveUser}
                        onCancel={this.handleCancelAddEdit}
                    />
                }
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return ({
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getLookupData: () => {
            return dispatch(UserActionCreators.getLookupData());
        },
        getUsers: (isAdmin, centerId, organType) => {
            return dispatch(UserActionCreators.getUsers(isAdmin, centerId, organType));
        },
        deleteUser: (isAdmin, centerId, userId) => {
            return dispatch(UserActionCreators.deleteUser(isAdmin, centerId, userId));
        },
        saveUser: (isAdmin, id, firstName, lastName, email, username, password, groupId, courierVendor, isMicrositeCoach, contactId, centerId, roles, sendTicketNotification, isDeactivated, organType, opoId) => {
            return dispatch(UserActionCreators.saveUser(isAdmin, id, firstName, lastName, email, username, password, groupId, courierVendor, isMicrositeCoach, contactId, centerId, roles, sendTicketNotification, isDeactivated, organType, opoId));
        },
        updatePasswordExpiration: (isAdmin, centerId, id, activate) => {
            return dispatch(UserActionCreators.updatePasswordExpiration(isAdmin, centerId, id, activate));
        },
        unblockUser: (isAdmin, centerId, userId, ipAddress) => {
            return dispatch(UserActionCreators.unblockUser(isAdmin, centerId, userId, ipAddress))
        },
        forceUnlockUser: (isAdmin, centerId, id) => {
            return dispatch(UserActionCreators.forceUnlockUser(isAdmin, centerId, id));
        },
        forceLockUser: (isAdmin, centerId, id) => {
            return dispatch(UserActionCreators.forceLockUser(isAdmin, centerId, id));
        },
    });
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(UserList);

